import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, ChevronRight } from "lucide-react";
import { EmptyState } from "@/components/EmptyState";
import { ErrorState } from "@/components/ErrorState";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { formatDate } from "@/lib/dates";
import { getAgenzieRete, getStoricoRete, isAccessoSospeso } from "@/api/rete";
import type { AgenziaInCarico, IncaricoStorico } from "@/types/rete";
import { AccessoSospeso } from "./AccessoSospeso";

// Come l'agenzia è entrata nella rete (rete_incarichi.origine).
const INGRESSO: Record<string, string> = {
  admin: "Assegnata",
  affiliazione: "Affiliazione",
};

export function AgenzieRete() {
  const navigate = useNavigate();
  const [agenzie, setAgenzie] = useState<AgenziaInCarico[] | null>(null);
  const [storico, setStorico] = useState<IncaricoStorico[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [sospeso, setSospeso] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([getAgenzieRete(), getStoricoRete()])
      .then(([a, s]) => {
        if (cancelled) return;
        setAgenzie(a);
        setStorico(s);
      })
      .catch((e) => {
        if (cancelled) return;
        if (isAccessoSospeso(e)) setSospeso(true);
        else setError("Impossibile caricare le agenzie.");
      });
    return () => { cancelled = true; };
  }, []);

  if (sospeso) return <AccessoSospeso />;
  if (error) return <ErrorState message={error} />;
  if (agenzie === null) return <p className="text-sm text-muted-foreground">Caricamento...</p>;

  // Lo storico mostra solo i periodi chiusi: quelli aperti sono già nella tabella sopra.
  const chiusi = storico.filter((s) => s.al !== null);

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-text">Agenzie in carico</h2>

      {agenzie.length === 0 ? (
        <EmptyState
          icon={<Building2 size={26} className="text-brand" />}
          title="Nessuna agenzia in carico"
          description="Le agenzie ti vengono assegnate dall'amministrazione oppure entrano dalle tue affiliazioni."
        />
      ) : (
        <div className="bg-card rounded-lg border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Agenzia</TableHead>
                  <TableHead>Dal</TableHead>
                  <TableHead className="hidden md:table-cell">Ingresso</TableHead>
                  <TableHead className="text-right">Operatori</TableHead>
                  <TableHead className="w-8" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {agenzie.map((a) => (
                  <TableRow
                    key={a.incarico_id}
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(`/rete/agenzie/${a.agenzia_id}/persone`)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate(`/rete/agenzie/${a.agenzia_id}/persone`);
                      }
                    }}
                    className="cursor-pointer hover:bg-muted/40 [&_td]:py-3"
                  >
                    <TableCell className="font-medium text-text">{a.agenzia_nome}</TableCell>
                    <TableCell className="tabular-nums">{formatDate(a.dal)}</TableCell>
                    <TableCell className="hidden md:table-cell text-muted-foreground">
                      {INGRESSO[a.origine] ?? a.origine}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{a.operatori ?? "—"}</TableCell>
                    <TableCell>
                      <ChevronRight size={16} className="text-muted-foreground" aria-hidden="true" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {chiusi.length > 0 && (
        <div className="bg-card rounded-lg border border-border overflow-hidden">
          <h3 className="font-semibold text-text px-4 pt-4 pb-3">Storico</h3>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Agenzia</TableHead>
                  <TableHead>Dal</TableHead>
                  <TableHead>Al</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {chiusi.map((s) => (
                  <TableRow key={s.incarico_id} className="[&_td]:py-3">
                    <TableCell className="text-text">{s.agenzia_nome}</TableCell>
                    <TableCell className="tabular-nums">{formatDate(s.dal)}</TableCell>
                    <TableCell className="tabular-nums">{formatDate(s.al)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
}
